// Helpers for a device's TM (tree management) report candidates.
//
// jnet2 firmware has shipped three formats with (almost) identical keys:
//   - ranked:              every candidate carries rank_idx 0..n-1 (0 = best);
//   - selected_only:       the candidate in use has rank_idx 0, all others -1;
//   - ranked_with_foreign: in-segment candidates rank 0..n-1, candidates
//                          from other segments rank in a tier from 100 up
//                          (101, 102, ... after in-segment rank 0); also
//                          adds Path_Distance.
// The backend normalises all three and adds `ranked` / `foreign_segment` /
// `selected` / `segment_id`, but older cached reports may lack them, so
// every helper here also derives the answer from rank_idx and
// selected_rank_id.

export const TM_UNRANKED = -1;
export const TM_FOREIGN_RANK_BASE = 100;
export const TM_NO_SEGMENT = 65535;

export const tmCandidateRank = (c) => {
  const r = c?.rank_idx ?? c?.RankIdx;
  return typeof r === 'number' && Number.isFinite(r) ? r : TM_UNRANKED;
};

export const isTmCandidateRanked = (c) =>
  typeof c?.ranked === 'boolean' ? c.ranked : tmCandidateRank(c) >= 0;

// A candidate from another segment (ranked_with_foreign tier).
export const isTmCandidateForeign = (c) =>
  (typeof c?.foreign_segment === 'boolean'
    ? c.foreign_segment
    : tmCandidateRank(c) >= TM_FOREIGN_RANK_BASE);

// The candidate's segment id, or null when unknown / unsegmented (65535).
export const tmCandidateSegmentId = (c) => {
  if (c?.segment_id !== undefined) return c.segment_id;
  const entry = Array.isArray(c?.segmentsID) ? c.segmentsID[0] : null;
  if (!entry || typeof entry !== 'object') return null;
  const id = Object.values(entry).find((v) => typeof v === 'number');
  return typeof id === 'number' && id !== TM_NO_SEGMENT ? id : null;
};

export const tmCandidatePathDistance = (c) => {
  const v = c?.Path_Distance ?? c?.path_distance;
  return typeof v === 'number' ? v : null;
};

export const tmSelectedRankId = (data) => {
  const v = data?.selected_rank_id ?? data?.selectedRankId;
  return typeof v === 'number' && Number.isFinite(v) ? v : TM_UNRANKED;
};

// A candidate is selected only when it is ranked and its rank is the
// report's selected_rank_id; an unranked (-1) slot never matches, even when
// selected_rank_id is itself -1.
export const isTmCandidateSelected = (c, selectedRankId) => {
  if (typeof c?.selected === 'boolean') return c.selected;
  const rank = tmCandidateRank(c);
  return rank >= 0 && rank === selectedRankId;
};

export const tmCandidateRSSI = (snr, nf) => (snr ?? 0) + (nf ?? 0);

const staRSSI = (c) => tmCandidateRSSI(c.STA_SNR ?? c.sta_snr, c.STA_NF ?? c.sta_nf);

// Display order: the selected candidate, then in-segment ranked candidates
// by rank, then foreign-segment ranked candidates by rank, then unranked
// candidates strongest-first (STA RSSI), so every firmware format reads
// the same way.
export const sortTmCandidates = (candidates, selectedRankId) =>
  [...(candidates || [])].sort((a, b) => {
    const aSel = isTmCandidateSelected(a, selectedRankId);
    const bSel = isTmCandidateSelected(b, selectedRankId);
    if (aSel !== bSel) return aSel ? -1 : 1;
    const aRanked = isTmCandidateRanked(a);
    const bRanked = isTmCandidateRanked(b);
    if (aRanked !== bRanked) return aRanked ? -1 : 1;
    if (aRanked) {
      const aForeign = isTmCandidateForeign(a);
      const bForeign = isTmCandidateForeign(b);
      if (aForeign !== bForeign) return aForeign ? 1 : -1;
      return tmCandidateRank(a) - tmCandidateRank(b);
    }
    return staRSSI(b) - staRSSI(a);
  });

// Rank label for the table: the rank, or a dash for unranked candidates.
export const tmCandidateRankLabel = (c) =>
  (isTmCandidateRanked(c) ? String(tmCandidateRank(c)) : '—');
